import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { updateBulkWidgetProperty, updateBulkWidgetsEditorState, updateWidgetEditorState, updateWidgetProperty } from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Button, FormCheck, Tab, Tabs } from "react-bootstrap";
import Tooltip from "components/Tooltip";
import { Property } from "../properties/";
import { ANIMATION_TYPES, EXIT_ANIMATION_TYPES } from "constants/animationTypes";

import { Animation as WidgetAnimation } from "@paperflite-marketing/cleverstory-commons";
import { useWidgetSelected } from "components/Editor/hooks/useEditorSession";
import { EditorContext } from "components/Editor";
import { HOTSPOT } from "constants/widgetTypes";
import { SLIDE_STORYBOARDS } from "constants/storyboardType";

import styles from "./animation.module.scss";

export const AnimationProperty = ({
  ids,
  widgets,
  position = "center",
  editorContext,
  name,
  exclusions = [],
  ...rest
}) => {
  const dispatch = useDispatch();

  const clipbaordData = window.localStorage.getItem("CLIPBOARD_ANIMATION");
  const value = clipbaordData ? JSON.parse(clipbaordData) : null;

  const [state, setState] = useState({
    animationType: "none",
    customize: false,
    activeTab: "entry",
    animating: false,
  });
  const [clipboardAnimation, setClipboardAnimation] = useState(value);

  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );
  const storyboardType = useSelector((state) =>
    _.get(state, `storyboards.storyboard.item.type`)
  );

  const widget = widgets[0];

  const sameAnimationType =
  widgets.length > 0 &&
  widgets.every((widget) => {
    return _.get(widget, `properties.${breakpoint}.animation.type`) === 
           _.get(widgets[0], `properties.${breakpoint}.animation.type`);
  });

  const animation =
    _.get(widget, `properties.${breakpoint}.animation`, {}) || {};
  const animationType = sameAnimationType ? _.get(animation, "type", "none") : 'none';

  const hotspotWidget = widgets.find(w => w.type === HOTSPOT);

  const widgetExitAnimation = _.get(widget, `properties.${breakpoint}.exitAnimation`);

  const clipboardType = _.get(clipboardAnimation, `animation.type`, '');
  const isParallax = clipboardType === 'parallax';
  const isSpin = clipboardType === 'spin';
  const isBounce = clipboardType === 'bounce' && state.activeTab === 'exit';

  // Copy animation conditions
  var copyAnimation = _.size(widgets) === 1 && (animation?.type || widgetExitAnimation?.type);

  // Paste animation conditions
  const hasClipboardAnimation = Boolean(clipboardAnimation?.animation);
  const hasSelectedWidgets = widgets.length > 0;
  
  const disallowHotspotAnimations = hotspotWidget && (isParallax || isSpin);
  const disallowHotspotExitBounce = hotspotWidget && isBounce;
  const disallowMultiWidgetParallax = widgets.length > 1 && isParallax;
  const disallowSpatialParallax = SLIDE_STORYBOARDS.includes(storyboardType) && isParallax;

  var pasteAnimation =
    hasClipboardAnimation && // there is something in clipboard
    hasSelectedWidgets && // there is at least one selected widget
    !disallowHotspotAnimations && // hotspot cannot have parallax or spin animation
    !disallowHotspotExitBounce && // hotspot cannot have exit bounce animation
    !disallowMultiWidgetParallax && // parallax cannot be applied to multiple widgets
    !disallowSpatialParallax; // parallax cannot be applied in spatial storyboard

  const preview = _.get(widget, `editorState.previewAnimation`);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "CLIPBOARD_ANIMATION") {
        setClipboardAnimation(e.newValue ? JSON.parse(e.newValue) : null);
      }
    };
  
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    // Only update if value actually changed
    setClipboardAnimation(prev => {
      if (_.isEqual(prev, value)) return prev; // no change
      return value; // update
    });
  }, [value]);

  const handleAnimation = (value, field) => {
    const previewType = _.get(preview, "type");
    const widgetIds = widgets.map((w) => w.uid);

    if (previewType !== animationType) {
      dispatch(updateBulkWidgetsEditorState(editorSession, widgetIds, {
        previewAnimation: null,
      }));
    }

    dispatch(updateBulkWidgetProperty(editorSession, widgetIds, breakpoint, {
      [field]: value
    }));

    setState((state) => ({
      ...state,
      customize: !_.isEmpty(value),
    }));
  };

  const handleHotspotAnimation = (widget, value, field) => {
    const widgetId = _.get(widget, "uid", '') || "";

    dispatch(updateWidgetProperty(editorSession, widgetId, breakpoint, {
      [field]: value
    }));

    setState((state) => ({
      ...state,
      customize: !_.isEmpty(value),
    }));
  };

  const handleTabChange = (key) => {
    setState((state) => ({ ...state, activeTab: key }));
  };

  const handlePreview = (value) => {
    if (!value || value.type === "parallax") {
      resetPreview();
      return;
    }

    resetPreview();

    const widgetIds = widgets.map((w) => w.uid);

    setTimeout(() => {
      dispatch(updateBulkWidgetsEditorState(editorSession, widgetIds, {
        previewAnimation: value,
      }));
    }, 500);
  };

  const resetPreview = () => {
    const widgetIds = widgets.map((w) => w.uid);

    dispatch(updateBulkWidgetsEditorState(editorSession, widgetIds, {
      previewAnimation: null,
    }));

    setState((state) => ({ ...state, animating: false }));
  };

  const handleBackClicked = () => {
    setState((state) => ({ ...state, customize: false }));
  };

  const handleClose = () => {
    rest.onCancel();
  };

  useEffect(() => {
    if (_.isEqual(preview, animation) || !state.animating) return;

    const widgetIds = widgets.map((w) => w.uid);

    dispatch(updateBulkWidgetsEditorState(editorSession, widgetIds, {
      previewAnimation: animation,
    }));

  }, [animation]);

  const handleCopyAnimation = (e, exit) => {
    editorContext.editorSession.eventBus.emit('copyAnimation', { exit: !!exit});
  };

  const handlePasteAnimation = (e, exit) => {
    editorContext.editorSession.eventBus.emit('pasteAnimation', { exit: !!exit});
  };

  if(widgets?.length === 0) return;

  return (
    <Property.Dialog
      className={styles.animationDialog}
      width={250}
      height={"auto"}
      target={`#${ids}`}
    >
      <Property.Header
        className={cx(styles.animationHeader, { [styles.customizeHeader]: state.customize })}
        title={state.customize ? "Settings" : "Animations"}
      >
        {state.customize && (
          <Button
            variant="info"
            className={styles.backButton}
            onClick={handleBackClicked}
          >
            <i className={cx("cleverstory-drop-down")} />
          </Button>
        )}
        <Button className={styles.action} onClick={handleClose}>
          <i className="cleverstory-close" />
        </Button>
      </Property.Header>

      {widget.type !== HOTSPOT && (
        <Property.Body className={styles.dialogBody}>
          {!state.customize && (
            <div className={styles.animationContainer}>
              <div className={styles.titleWrapper}>
                {animationType !== "none" ? (
                  <span className={styles.animationTypeSpan}>
                    {animationType === "puffin" ? "Puff-In" : animationType}
                  </span>
                ) : (
                  <h5>Choose a style</h5>
                )}
                <Button
                  variant="info"
                  disabled={!copyAnimation}
                  onClick={handleCopyAnimation}
                >
                  <i className={cx("cleverstory-copy-animation")} />
                </Button>
                <Button
                  variant="info"
                  disabled={!pasteAnimation}
                  onClick={handlePasteAnimation}
                >
                  <i className={cx("cleverstory-paste-animation")} />
                </Button>
              </div>
              <div className={styles.grid}>
                {ANIMATION_TYPES.filter((item) => {
                  if (widgets.length > 1 && item.type === "parallax") {
                    return false;
                  }

                  return !exclusions.includes(item.type) && !item.exit;
                }).map((item, index) => {
                  const active = animationType === item.type;
                  let value = active ? animation : item.defaultAnimation;

                  if (
                    SLIDE_STORYBOARDS.includes(storyboardType) &&
                    item.type === "parallax"
                  )
                    return null;

                  return (
                    <div
                      key={index}
                      className={cx(styles.animationTypeButton, {
                        [styles.active]: active,
                        [styles.none]: item.type === "none", // for styling purpose
                        [styles.activeNone]:
                          animationType === "none" && item.type === "none", // for styling purpose
                      })}
                      onMouseEnter={() => handlePreview(value)}
                      onMouseLeave={resetPreview}
                      onClick={() => handleAnimation(value, "animation")}
                    >
                      <div className={cx(styles.borderAnimation)} />
                      <div
                        className={cx(styles.icon, {
                          [styles.biggerIcon]: ["expand", "puffin"].includes(
                            item.type
                          ),
                        })}
                      >
                        <img src={item.buttonImg} />
                      </div>
                      <span className={styles.animationName}>{item.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {state.customize && (
            <AnimationCustomizeSettings
              exit={state.activeTab === "entry" ? false : true}
              editorContext={editorContext}
              widgets={widgets}
              handlePreview={handlePreview}
              resetPreview={resetPreview}
              state={state}
              setState={setState}
              copyAnimation={copyAnimation}
              pasteAnimation={pasteAnimation}
              handleCopyAnimation={handleCopyAnimation}
              handlePasteAnimation={handlePasteAnimation}
            />
          )}
        </Property.Body>
      )}

      {widget.type === HOTSPOT && (
        <Property.Body className={styles.dialogBody}>
          {!state.customize && (
            <HotSpotAnimationSettings
              widgets={widgets}
              breakpoint={breakpoint}
              activeTab={state.activeTab}
              storyboardType={storyboardType}
              handleTabChange={handleTabChange}
              exclusions={exclusions}
              handlePreview={handlePreview}
              resetPreview={resetPreview}
              handleAnimation={handleHotspotAnimation}
              copyAnimation={copyAnimation}
              pasteAnimation={pasteAnimation}
              handleCopyAnimation={handleCopyAnimation}
              handlePasteAnimation={handlePasteAnimation}
            />
          )}
          {state.customize && (
            <AnimationCustomizeSettings
              state={state}
              exit={state.activeTab === "entry" ? false : true}
              setState={setState}
              widgets={widgets}
              handlePreview={handlePreview}
              resetPreview={resetPreview}
              editorContext={editorContext}
              copyAnimation={copyAnimation}
              pasteAnimation={pasteAnimation}
              handleCopyAnimation={handleCopyAnimation}
              handlePasteAnimation={handlePasteAnimation}
            />
          )}
        </Property.Body>
      )}
    </Property.Dialog>
  );
};

const AnimationCustomizeSettings = ({ 
  resetPreview, 
  editorContext, 
  state, 
  setState, 
  widgets, 
  handlePreview, 
  exit = false,
  pasteAnimation,
  copyAnimation,
  handleCopyAnimation,
  handlePasteAnimation
}) => {
  const dispatch = useDispatch();

  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );

  const widget = widgets[0];
  const animation = exit
    ? _.get(widget, `properties.${breakpoint}.exitAnimation`, {}) || {}
    : _.get(widget, `properties.${breakpoint}.animation`, {}) || {};

  const animationType = _.get(animation, "type", "none");
  const preview = _.get(widget, `editorState.previewAnimation`);

  const animationConfiguration =
    ANIMATION_TYPES.find((data) => data.type === animationType) || {};
  const directionOptions = _.get(animationConfiguration, "directions");

  const handlePropertyChange = (field, value) => {
    const widgetIds = widgets.map((w) => w.uid);

    dispatch(
      updateBulkWidgetProperty(editorSession, widgetIds, breakpoint, {
        [exit ? "exitAnimation" : "animation"]: {
          ...animation,
          [field]: value
        },
      })
    );
  };

  return (
    <div className={styles.customizeDialog}>
      <div className={styles.titleWrapper}>
        {animationType !== "none" ? <span className={styles.animationTypeSpan}>
          {animationType === 'puffin' ? 'Puff-In' : animationType}</span> : <h5>Choose a style</h5>}

          <Button
            variant="info"
            disabled={!copyAnimation}
            onClick={() => exit ? handleCopyAnimation(_, 'exit') : handleCopyAnimation()}
          >
            <i className={cx("cleverstory-copy-animation")} />
          </Button>
          <Button
            variant="info"
            disabled={!pasteAnimation}
            onClick={() => exit ? handlePasteAnimation(_, 'exit') : handlePasteAnimation()}
          >
            <i className={cx("cleverstory-paste-animation")} />
          </Button>
        </div>
      <div className={styles.animationType}>
        <span className={styles.name}>
          {_.get(animationConfiguration, "name", animationType)}
        </span>
        {animation.type === "parallax" && (
          <Tooltip placement="top" content={"Scroll to preview animation"}>
            <div>
              <Property.Switch
                id="parallax"
                value={preview != null && state.animating}
                onChange={(data) => {
                  if (data && !state.animating) {
                    setState((state) => ({
                      ...state,
                      animating: true,
                    }));
                    dispatch(
                      updateWidgetEditorState(editorSession, widget.uid, {
                        previewAnimation: animation,
                      })
                    );
                  } else {
                    resetPreview();
                  }
                }}
              />
            </div>
          </Tooltip>
        )}
        {animation.type !== "parallax" && (
          <Button
            variant="info"
            onClick={() => {
              let animationData = animation;
              if (exit) {
                animationData = {
                  ...animationData,
                  exit: true,
                };
              }
              handlePreview(animationData);
            }}
            className={styles.previewButton}
          >
            <i className={cx("cleverstory-play")} />
          </Button>
        )}
      </div>
      {directionOptions && (
        <Property name="Direction" className={styles.animationDurationProperty}>
          <div className={styles.animationDirection}>
            <div className={styles.options}>
              {directionOptions.map((item, index) => (
                <button
                  key={index}
                  className={cx(styles.directions, {
                    [styles.active]: animation.direction === item.value,
                  })}
                  onClick={() => {
                    handlePropertyChange("direction", _.get(item, "value"));
                  }}
                >
                  <i className={cx(`cleverstory-${item.icon}`)}></i>
                </button>
              ))}
              {animationType === "parallax" && (
                <button
                  className={cx(styles.directions, {
                    [styles.active]: animation.direction === "angle",
                  })}
                  onClick={() => {
                    handlePropertyChange("direction", "angle");
                  }}
                >
                  <i className={cx(`cleverstory-degree`)}></i>
                </button>
              )}
            </div>
          </div>
        </Property>
      )}
      {animationType === "slide" && (
        <Property name="Distance" className={styles.animationDurationProperty}>
          <Property.Slider
            input
            min={5}
            className={styles.customSlider}
            max={300}
            step={1}
            value={animation.distance}
            onChange={(data) => handlePropertyChange("distance", data)}
          />
        </Property>
      )}
      {animationType === "spin" && (
        <Property name="Spins" className={styles.animationDurationProperty}>
          <Property.Slider
            input
            min={1}
            className={styles.customSlider}
            max={15}
            step={1}
            value={animation.count}
            onChange={(data) => handlePropertyChange("count", data)}
          />
        </Property>
      )}
      {animationType !== "parallax" && (
        <>
          <Property name="Duration" className={styles.animationDurationProperty}>
            <Property.Slider
              input
              min={0.1}
              max={5}
              step={0.1}
              value={animation.duration}
              onChange={(data) => handlePropertyChange("duration", data)}
            />
          </Property>
          <Property name="Delay" className={styles.animationDurationProperty}>
            <Property.Slider
              input
              min={0}
              max={10}
              step={0.1}
              value={animation.delay}
              onChange={(data) => handlePropertyChange("delay", data)}
            />
          </Property>
          <Property className={styles.animateToggle}>
            <Property.Switch
              id="animate"
              title={"Only animate first time"}
              value={animation.triggerOnce}
              onChange={() =>
                handlePropertyChange("triggerOnce", !animation.triggerOnce)
              }
            />
          </Property>
        </>
      )}
      {animationType === "parallax" && animation.direction === "angle" && (
        <Property name="Degree" className={styles.rotationControlProperty}>
          <Property.Rotation
            input
            min={0}
            max={360}
            step={1}
            value={animation.angle}
            onChange={(data) => handlePropertyChange("angle", data)}
          />
        </Property>
      )}
      {animationType === "parallax" && (
        <>
          <Property name="Speed" className={styles.animationDurationProperty}>
            <Property.Slider
              input
              min={1}
              className={styles.customSlider}
              max={10}
              step={1}
              value={animation.speed}
              onChange={(data) =>
                handlePropertyChange("speed", parseInt(data))
              }
            />
          </Property>
          <Property
            name="Assign current position of element as"
            className={styles.animationPositionProperty}
          >
            <Property.RadioButton
              className={styles.radio}
              title="Start position"
              value={animation.reverse}
              onClick={() => handlePropertyChange("reverse", true)}
            />
            <Property.RadioButton
              className={styles.radio}
              title="End position"
              value={!animation.reverse}
              onClick={() => handlePropertyChange("reverse", false)}
            />
          </Property>
        </>
      )}
    </div>
  );
};

const HotSpotAnimationSettings = ({
  exclusions,
  handleCopyAnimation,
  handlePasteAnimation,
  copyAnimation,
  pasteAnimation,
  handleAnimation,
  handleTabChange,
  breakpoint,
  activeTab,
  handlePreview,
  resetPreview,
  widgets,
  storyboardType,
}) => {
  const widget = widgets[0];
  const animation =
    activeTab === "entry"
      ? _.get(widget, `properties.${breakpoint}.animation`, {}) || {}
      : _.get(widget, `properties.${breakpoint}.exitAnimation`, {}) || {};

  const animationType = _.get(animation, "type", "none");

  return (
    <Tabs
      activeKey={activeTab}
      onSelect={handleTabChange}
      className={styles.buttonStyleTabs}
    >
      <Tab eventKey="entry" title="Entry" className={styles.buttonStyleTab}>
        <div className={styles.animationContainer}>
          <div className={styles.titleWrapper}>
          {animationType !== "none" ? <span className={styles.animationTypeSpan}>{animationType === 'puffin' ? 'Puff-In' : animationType}</span> : <h5>Choose a style</h5>}

            <Button
              variant="info"
              disabled={!copyAnimation}
              onClick={handleCopyAnimation}
            >
              <i className={cx("cleverstory-copy-animation")} />
            </Button>
            <Button
              variant="info"
              disabled={!pasteAnimation}
              onClick={handlePasteAnimation}
            >
              <i className={cx("cleverstory-paste-animation")} />
            </Button>
          </div>
          <div className={styles.grid}>
            {ANIMATION_TYPES.filter(
              (item) => !exclusions.includes(item.type)
            ).map((item, index) => {
              const active = animationType === item.type;
              let value = active ? animation : item.defaultAnimation;

              if (SLIDE_STORYBOARDS.includes(storyboardType) && item.type === "parallax") {
                return null;
              }

              return (
                <div
                  key={index}
                  active={active}
                  className={cx(styles.animationTypeButton, {
                    [styles.active]: active,
                    [styles.none]: item.type === "none", // for styling purpose
                    [styles.activeNone]:
                      animationType === "none" && item.type === "none", // for styling purpose
                  })}
                  onMouseEnter={() => handlePreview(value)}
                  onMouseLeave={() => resetPreview()}
                  onClick={() => {
                    widgets.forEach((widget) => {
                      handleAnimation(widget, value, "animation");
                    });
                  }}
                >
                  <div className={cx(styles.borderAnimation)} />
                  <div
                    className={cx(styles.icon, {
                      [styles.biggerIcon]:
                        item.type === "expand" || item.type === "puffin",
                    })}
                  >
                    <img src={item.buttonImg} />
                  </div>
                  <span className={styles.animationName}>{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Tab>
      <Tab eventKey="exit" title="Exit" className={styles.buttonStyleTab}>
        <div className={styles.animationContainer}>
        <div className={styles.titleWrapper}>
            {animationType !== "none" ? <span className={styles.animationTypeSpan}>{animationType === 'puffin' ? 'Puff-In' : animationType}</span> : <h5>Choose a style</h5>}

            <Button
              variant="info"
              disabled={!copyAnimation}
              onClick={(e) => handleCopyAnimation(e, 'exit')}
            >
              <i className={cx("cleverstory-copy-animation")} />
            </Button>
            <Button
              variant="info"
              disabled={!pasteAnimation}
              onClick={(e) => handlePasteAnimation(e, 'exit')}
            >
              <i className={cx("cleverstory-paste-animation")} />
            </Button>
          </div>
          <div className={styles.grid}>
            {EXIT_ANIMATION_TYPES.filter(
              (item) => ![...exclusions].includes(item.type)
            ).map((item, index) => {
              const active = animationType === item.type;
              let value = active ? animation : item.defaultAnimation;

              if (SLIDE_STORYBOARDS.includes(storyboardType) && item.type === "parallax") {
                return null;
              }

              return (
                <div
                  key={index}
                  active={active}
                  className={cx(styles.animationTypeButton, {
                    [styles.active]: active,
                    [styles.none]: item.type === "none", // for styling purpose
                    [styles.activeNone]:
                      animationType === "none" && item.type === "none", // for styling purpose
                  })}
                  onMouseEnter={() => handlePreview({ ...value, exit: true })}
                  onMouseLeave={() => resetPreview()}
                  onClick={() => {
                    widgets.forEach((widget) => {
                      handleAnimation(widget, value, "exitAnimation");
                    });
                  }}
                >
                  <div className={cx(styles.borderAnimation)} />
                  <div
                    className={cx(styles.icon, {
                      [styles.biggerIcon]:
                        item.type === "expand" || item.type === "puffin",
                    })}
                  >
                    <img src={item.buttonImg} />
                  </div>
                  <span className={styles.animationName}>{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Tab>
    </Tabs>
  );
};


export const Animation = ({ widget, children, exit=false }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );
  const animationData = _.get(widget, `properties.${breakpoint}.animation`);
  const previewAnimationData = _.get(widget, `editorState.previewAnimation`);

  const animationToRender = previewAnimationData
    ? previewAnimationData
    : animationData;
  const type = _.get(animationToRender, "type") || "none";
  const uid = _.get(widget, "uid");

  const selected = useWidgetSelected(editorSession, uid);
  useEffect(() => {
    if (!selected && previewAnimationData) {
      dispatch(
        updateWidgetEditorState(editorSession, uid, {
          previewAnimation: null,
        })
      );
    }
  }, [selected, previewAnimationData]);

  if (!previewAnimationData || !widget) {
    return children;
  }

  const options = {
    ...previewAnimationData,
  };

  return (
    <WidgetAnimation key={type} {...options}>
      {children}
    </WidgetAnimation>
  );
};
